/**
 * Backend Transaction Service - Handles real merchant transaction operations
 * Fetches actual transaction data from the backend API
 */

import axios from 'axios';
import api from '../api/api';

class BackendTransactionService {
  constructor() {
    this.cache = new Map(); // Cache for transaction data
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes cache
  }

  /**
   * Get authentication headers — returns Authorization header in header mode.
   */
  getAuthHeaders() {
    const token = localStorage.getItem('token');
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  }

  /**
   * Fetch transactions for a specific merchant from backend
   */
  async fetchMerchantTransactions(merchantId) {
    try {
      console.log('🔍 Fetching transactions for merchant:', merchantId);
      
      const headers = this.getAuthHeaders();

      // Check if user is admin to determine which endpoint to use
      // We no longer read from localStorage — pass role from caller or try admin endpoint first
      let response;

      try {
        // Try admin endpoint first
        response = await axios.get(`/api/payment/admin/merchant/${merchantId}`, { headers });
        console.log('✅ Admin endpoint response:', response.data);
        console.log('📊 Transactions returned for merchant', merchantId, ':', response.data.data?.length || 0);
      } catch (adminError) {
        // If admin endpoint fails (403/401), try merchant endpoint
        console.log('👤 Merchant user - fetching own transactions');
        response = await axios.get('/api/payment/merchant/list', { headers });
        console.log('✅ Merchant endpoint response:', response.data);
      }
      
      if (response.data.success) {
        const transactions = response.data.data;
        console.log('✅ Raw transactions from backend:', transactions);
        console.log('📊 Transaction count:', transactions.length);
        
        if (transactions.length > 0) {
          console.log('📋 Sample transaction structure:', transactions[0]);
        }
        
        // Transform backend data to match frontend format - PRESERVE EXACT DATA
        const transformedTransactions = transactions.map((t, index) => {
          console.log(`🔄 Transforming transaction ${index}:`, t);
          console.log(`📧 Customer email from backend: '${t.customerEmail}'`);
          console.log(`📱 Customer phone from backend: '${t.customerPhone}'`);
          
          return {
            // Use EXACT backend data - no fake/demo data
            id: t.transactionId || t.id,
            merchantId: t.merchantId || merchantId,
            orderId: t.orderId || `ORDER_${t.transactionId || t.id}`,
            amount: parseFloat(t.amount || 0),
            // Actual amount credited by the bank (from callback/response). Kept as
            // null when not provided so reconciliation can tell "no response amount"
            // apart from "paid 0".
            paidAmount: (t.paidAmount !== null && t.paidAmount !== undefined && t.paidAmount !== '')
              ? parseFloat(t.paidAmount)
              : null,
            utr: t.utr || null,
            currency: t.currency || 'INR',
            status: this.mapBackendStatus(t.status),
            paymentMethod: this.mapBackendPaymentMethod(t.paymentMethod),
            // CRITICAL FIX: Use EXACT customer data from backend - NO FALLBACKS
            customerEmail: t.customerEmail, // Use exact backend value, even if null/undefined
            customerPhone: t.customerPhone, // Use exact backend value, even if null/undefined
            customerName: t.customerName, // Include customer name if available
            description: t.description || 'Payment transaction',
            createdAt: t.createdAt || new Date().toISOString(),
            updatedAt: t.updatedAt || t.createdAt || new Date().toISOString(),
            fees: t.fees || (t.status === 'SUCCESS' ? Math.floor(parseFloat(t.amount || 0) * 0.02) : 0),
            netAmount: t.netAmount || (parseFloat(t.amount || 0) - (t.status === 'SUCCESS' ? Math.floor(parseFloat(t.amount || 0) * 0.02) : 0)),
            gateway: t.bankProvider || 'Payment Gateway',
            gatewayTransactionId: t.gatewayTransactionId || t.bankTransactionId || t.transactionId,
            refundAmount: t.refundAmount || 0,
            // Preserve all original backend data
            originalBackendData: t,
            metadata: {
              userAgent: 'Web Browser',
              ipAddress: '192.168.1.1',
              source: 'backend'
            }
          };
        });

        console.log('✅ Transformed transactions:', transformedTransactions);

        // Cache the results
        this.cache.set(merchantId, {
          data: transformedTransactions,
          timestamp: Date.now()
        });

        return transformedTransactions;
      } else {
        console.error('❌ Backend API returned error:', response.data);
        return [];
      }
    } catch (error) {
      console.error('❌ Error fetching merchant transactions:', error);
      console.error('❌ Error details:', error.response?.data);
      
      // Return cached data if available
      const cached = this.cache.get(merchantId);
      if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
        console.log('📋 Returning cached transactions');
        return cached.data;
      }
      
      // If no backend data available, return empty array
      console.log('⚠️ No transactions available, returning empty array');
      return [];
    }
  }

  /**
   * Get transactions for a merchant (with caching)
   */
  async getMerchantTransactions(merchantId) {
    try {
      // ALWAYS fetch fresh data for admin view - disable cache for now
      console.log('🔄 Fetching FRESH transactions (cache disabled for debugging)');
      return await this.fetchMerchantTransactions(merchantId);
      
      // TODO: Re-enable cache after debugging
      // Check cache first
      // const cached = this.cache.get(merchantId);
      // if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
      //   console.log('📋 Using cached transactions for merchant:', merchantId);
      //   return cached.data;
      // }

      // Fetch from backend
      // return await this.fetchMerchantTransactions(merchantId);
    } catch (error) {
      console.error('Error getting merchant transactions:', error);
      return [];
    }
  }

  /**
   * Get transaction statistics for a merchant
   */
  async getMerchantTransactionStats(merchantId) {
    try {
      const transactions = await this.getMerchantTransactions(merchantId);
      
      const stats = {
        totalTransactions: transactions.length,
        successfulTransactions: transactions.filter(t => t.status === 'SUCCESS').length,
        pendingTransactions: transactions.filter(t => t.status === 'PENDING').length,
        failedTransactions: transactions.filter(t => t.status === 'FAILED').length,
        refundedTransactions: transactions.filter(t => t.status === 'REFUNDED').length,
        totalAmount: transactions.reduce((sum, t) => sum + (t.status === 'SUCCESS' ? t.amount : 0), 0),
        totalFees: transactions.reduce((sum, t) => sum + (t.status === 'SUCCESS' ? t.fees : 0), 0),
        totalRefunds: transactions.reduce((sum, t) => sum + t.refundAmount, 0),
        averageTransactionAmount: 0,
        successRate: 0
      };
      
      if (stats.totalTransactions > 0) {
        stats.averageTransactionAmount = Math.round(stats.totalAmount / stats.successfulTransactions) || 0;
        stats.successRate = Math.round((stats.successfulTransactions / stats.totalTransactions) * 100);
      }
      
      return stats;
    } catch (error) {
      console.error('Error calculating transaction stats:', error);
      return {
        totalTransactions: 0,
        successfulTransactions: 0,
        pendingTransactions: 0,
        failedTransactions: 0,
        refundedTransactions: 0,
        totalAmount: 0,
        totalFees: 0,
        totalRefunds: 0,
        averageTransactionAmount: 0,
        successRate: 0
      };
    }
  }

  /**
   * Get all merchants' transaction statistics (for admin)
   */
  async getAllMerchantsTransactionStats() {
    try {
      const headers = this.getAuthHeaders();
      if (!headers.Authorization) {
        console.error('❌ No authentication token found');
        return this.getEmptyStats();
      }

      // Fetch admin stats from backend
      const response = await axios.get('/api/payment/admin/stats', { headers });
      
      console.log('📊 Admin stats response:', response.data);
      
      if (response.data.success) {
        const backendStats = response.data.data;
        console.log('📊 Backend stats data:', backendStats);
        
        return {
          totalTransactions: backendStats.totalPayments || 0,
          successfulTransactions: backendStats.successfulPayments || 0,
          pendingTransactions: backendStats.pendingPayments || 0,
          failedTransactions: backendStats.failedPayments || 0,
          refundedTransactions: backendStats.refundedPayments || 0,
          totalAmount: backendStats.totalAmount || 0,
          totalFees: backendStats.totalFees || 0,
          totalRefunds: backendStats.totalRefunds || 0,
          totalMerchants: backendStats.totalMerchants || 0
        };
      } else {
        console.error('❌ Backend admin stats API returned error:', response.data);
        return this.getEmptyStats();
      }
    } catch (error) {
      console.error('❌ Error getting all merchants transaction stats:', error);
      console.error('❌ Error details:', error.response?.data);
      return this.getEmptyStats();
    }
  }

  /**
   * Search transactions by various criteria
   */
  async searchTransactions(merchantId, filters = {}) {
    try {
      console.log('🔍 Searching backend transactions with filters:', filters);
      let transactions = await this.getMerchantTransactions(merchantId);
      console.log('📊 Initial backend transactions count:', transactions.length);
      
      if (transactions.length === 0) {
        console.log('⚠️ No backend transactions found for merchant:', merchantId);
        return [];
      }
      
      // Log sample transaction structure for debugging
      if (transactions.length > 0) {
        console.log('📋 Sample backend transaction structure:', {
          id: transactions[0].id,
          createdAt: transactions[0].createdAt,
          status: transactions[0].status,
          paymentMethod: transactions[0].paymentMethod,
          amount: transactions[0].amount
        });
      }
      
      // Apply filters one by one with detailed logging
      let filteredTransactions = [...transactions];
      
      // Apply time period filter first
      if (filters.timePeriod && filters.timePeriod !== '') {
        const dateRange = this.getDateRangeForPeriod(filters.timePeriod);
        console.log('📅 Date range for period:', filters.timePeriod, dateRange);
        if (dateRange) {
          const beforeCount = filteredTransactions.length;
          filteredTransactions = filteredTransactions.filter(t => {
            const transactionDate = new Date(t.createdAt);
            const isInRange = transactionDate >= dateRange.start && transactionDate <= dateRange.end;
            if (!isInRange && beforeCount <= 5) {
              console.log(`📅 Transaction ${t.id} (${transactionDate.toISOString()}) not in range ${dateRange.start.toISOString()} - ${dateRange.end.toISOString()}`);
            }
            return isInRange;
          });
          console.log(`📅 After time filter (${filters.timePeriod}): ${beforeCount} → ${filteredTransactions.length}`);
        }
      }
      
      // Apply status filter
      if (filters.status && filters.status !== '') {
        const beforeCount = filteredTransactions.length;
        filteredTransactions = filteredTransactions.filter(t => {
          const matches = t.status === filters.status;
          if (!matches && beforeCount <= 5) {
            console.log(`🔄 Transaction ${t.id} status '${t.status}' doesn't match filter '${filters.status}'`);
          }
          return matches;
        });
        console.log(`🔄 After status filter (${filters.status}): ${beforeCount} → ${filteredTransactions.length}`);
      }
      
      // Apply payment method filter
      if (filters.paymentMethod && filters.paymentMethod !== '') {
        const beforeCount = filteredTransactions.length;
        filteredTransactions = filteredTransactions.filter(t => {
          const matches = t.paymentMethod === filters.paymentMethod;
          if (!matches && beforeCount <= 5) {
            console.log(`💳 Transaction ${t.id} method '${t.paymentMethod}' doesn't match filter '${filters.paymentMethod}'`);
          }
          return matches;
        });
        console.log(`💳 After payment method filter (${filters.paymentMethod}): ${beforeCount} → ${filteredTransactions.length}`);
      }
      
      // Apply date range filters
      if (filters.dateFrom) {
        const beforeCount = filteredTransactions.length;
        const fromDate = new Date(filters.dateFrom);
        filteredTransactions = filteredTransactions.filter(t => {
          const transactionDate = new Date(t.createdAt);
          const matches = transactionDate >= fromDate;
          if (!matches && beforeCount <= 5) {
            console.log(`📅 Transaction ${t.id} date ${transactionDate.toISOString()} before ${fromDate.toISOString()}`);
          }
          return matches;
        });
        console.log(`📅 After dateFrom filter (${filters.dateFrom}): ${beforeCount} → ${filteredTransactions.length}`);
      }
      
      if (filters.dateTo) {
        const beforeCount = filteredTransactions.length;
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999); // End of day
        filteredTransactions = filteredTransactions.filter(t => {
          const transactionDate = new Date(t.createdAt);
          const matches = transactionDate <= toDate;
          if (!matches && beforeCount <= 5) {
            console.log(`📅 Transaction ${t.id} date ${transactionDate.toISOString()} after ${toDate.toISOString()}`);
          }
          return matches;
        });
        console.log(`📅 After dateTo filter (${filters.dateTo}): ${beforeCount} → ${filteredTransactions.length}`);
      }
      
      // Apply amount filters
      if (filters.minAmount && !isNaN(filters.minAmount)) {
        const beforeCount = filteredTransactions.length;
        const minAmount = parseFloat(filters.minAmount);
        filteredTransactions = filteredTransactions.filter(t => {
          const matches = t.amount >= minAmount;
          if (!matches && beforeCount <= 5) {
            console.log(`💰 Transaction ${t.id} amount ${t.amount} below minimum ${minAmount}`);
          }
          return matches;
        });
        console.log(`💰 After minAmount filter (${filters.minAmount}): ${beforeCount} → ${filteredTransactions.length}`);
      }
      
      if (filters.maxAmount && !isNaN(filters.maxAmount)) {
        const beforeCount = filteredTransactions.length;
        const maxAmount = parseFloat(filters.maxAmount);
        filteredTransactions = filteredTransactions.filter(t => {
          const matches = t.amount <= maxAmount;
          if (!matches && beforeCount <= 5) {
            console.log(`💰 Transaction ${t.id} amount ${t.amount} above maximum ${maxAmount}`);
          }
          return matches;
        });
        console.log(`💰 After maxAmount filter (${filters.maxAmount}): ${beforeCount} → ${filteredTransactions.length}`);
      }
      
      // Apply search term filter
      if (filters.searchTerm && filters.searchTerm.trim() !== '') {
        const beforeCount = filteredTransactions.length;
        const term = filters.searchTerm.toLowerCase().trim();
        filteredTransactions = filteredTransactions.filter(t => {
          const searchableFields = [
            t.id,
            t.orderId,
            t.customerEmail,
            t.description,
            t.gatewayTransactionId
          ].filter(field => field != null).map(field => field.toString().toLowerCase());
          
          const matches = searchableFields.some(field => field.includes(term));
          if (!matches && beforeCount <= 5) {
            console.log(`🔍 Transaction ${t.id} doesn't match search term '${term}' in fields:`, searchableFields);
          }
          return matches;
        });
        console.log(`🔍 After search filter (${filters.searchTerm}): ${beforeCount} → ${filteredTransactions.length}`);
      }
      
      console.log('✅ Final filtered backend transactions:', filteredTransactions.length);
      return filteredTransactions;
    } catch (error) {
      console.error('❌ Error searching backend transactions:', error);
      return [];
    }
  }

  /**
   * Get date range for time period filter
   */
  getDateRangeForPeriod(period) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    console.log('📅 Calculating date range for period:', period);
    
    switch (period) {
      case 'today':
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);
        return { start: today, end: todayEnd };
        
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        return { start: weekStart, end: weekEnd };
        
      case 'month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
        return { start: monthStart, end: monthEnd };
        
      case 'year':
        const yearStart = new Date(today.getFullYear(), 0, 1);
        const yearEnd = new Date(today.getFullYear(), 11, 31, 23, 59, 59, 999);
        return { start: yearStart, end: yearEnd };
        
      case 'lifetime':
        return null; // No date filtering
        
      default:
        return null;
    }
  }

  /**
   * Clear cache for a merchant
   */
  clearMerchantCache(merchantId) {
    this.cache.delete(merchantId);
    console.log('🗑️ Cleared cache for merchant:', merchantId);
  }

  /**
   * Clear all cached data
   */
  clearAllCache() {
    this.cache.clear();
    console.log('🗑️ Cleared all transaction cache');
  }

  /**
   * Force refresh transactions for a merchant (bypass cache)
   */
  async forceRefreshMerchantTransactions(merchantId) {
    console.log('🔄 Force refreshing transactions for merchant:', merchantId);
    this.clearMerchantCache(merchantId);
    return await this.fetchMerchantTransactions(merchantId);
  }

  /**
   * Get empty stats object
   */
  getEmptyStats() {
    return {
      totalTransactions: 0,
      successfulTransactions: 0,
      pendingTransactions: 0,
      failedTransactions: 0,
      refundedTransactions: 0,
      totalAmount: 0,
      totalFees: 0,
      totalRefunds: 0,
      totalMerchants: 0
    };
  }

  /**
   * Generate mock transactions (fallback for demo purposes)
   */
  generateMockTransactions(merchantId, count = 20) {
    console.log('⚠️ Generating mock transactions as fallback');
    // This is a fallback - in production, this should not be used
    // Instead, the backend should have actual transaction data
    return [];
  }

  /**
   * Map backend status to frontend status
   */
  mapBackendStatus(backendStatus) {
    if (!backendStatus) return 'PENDING';
    
    // Handle enum format (e.g., "SUCCESS", "PENDING", etc.)
    const status = backendStatus.toString().toUpperCase();
    
    switch (status) {
      case 'SUCCESS':
      case 'COMPLETED':
        return 'SUCCESS';
      case 'PENDING':
      case 'PROCESSING':
        return 'PENDING';
      case 'FAILED':
      case 'FAILURE':
      case 'DECLINED':
        return 'FAILED';
      case 'REFUNDED':
        return 'REFUNDED';
      case 'CANCELLED':
      case 'CANCELED':
        return 'FAILED';
      default:
        return 'PENDING';
    }
  }

  /**
   * Map backend payment method to frontend payment method
   */
  mapBackendPaymentMethod(backendMethod) {
    if (!backendMethod) return 'UPI';
    
    const method = backendMethod.toString().toUpperCase();
    
    switch (method) {
      case 'UPI_QR':
      case 'UPI_ID':
      case 'UPI_INTENT':
      case 'UPI':
        return 'UPI';
      case 'CARD':
      case 'CREDIT_CARD':
      case 'DEBIT_CARD':
        return 'CARD';
      case 'NET_BANKING':
      case 'NETBANKING':
        return 'NET_BANKING';
      case 'WALLET':
        return 'WALLET';
      default:
        return 'UPI';
    }
  }
}

// Create singleton instance
const backendTransactionService = new BackendTransactionService();
export default backendTransactionService;